import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, Clock as ClockIcon, Filter, MapPin, Loader2, RefreshCw } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Break Approvals — compact one-line table for fast bulk review.
 *
 * Top filters work across the whole org (no staff gate): Geo location +
 * Date range + Break type + Status tabs. Admin can scan dozens of break
 * excesses per page and Approve / Waive / Reject inline.
 */

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', tone: 'bg-orange-100 text-orange-700 border-orange-300' },
  { id: 'approved', label: 'Approved', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'waived', label: 'Waived', tone: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'rejected', label: 'Rejected', tone: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'all', label: 'All', tone: 'bg-gray-100 text-gray-700 border-gray-300' },
];

const STATUS_BADGE = {
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-emerald-100 text-emerald-700',
  waived: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
};

const TYPE_TONE = {
  lunch: 'bg-orange-50 text-orange-700',
  tea: 'bg-amber-50 text-amber-700',
  permission: 'bg-purple-50 text-purple-700',
  dinner: 'bg-rose-50 text-rose-700',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);

const formatDuration = (hrs) => {
  const totalMin = Math.round((hrs || 0) * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

export default function BreakApprovals() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all'); // all | lunch | tea | permission | dinner
  const [geoFilter, setGeoFilter] = useState('');
  const [geoOptions, setGeoOptions] = useState([]);
  const [dateRange, setDateRange] = useState({ from: daysAgoISO(6), to: todayISO() });
  const [editing, setEditing] = useState(null);  // record id
  const [penalty, setPenalty] = useState('');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);

  // Load geo-fence options once.
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/geofences`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setGeoOptions(await r.json());
      } catch {}
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusTab !== 'all') params.append('status', statusTab);
      if (typeFilter !== 'all') params.append('break_type', typeFilter);
      if (geoFilter) params.append('geo_fence_id', geoFilter);
      if (dateRange.from) params.append('from_date', dateRange.from);
      if (dateRange.to) params.append('to_date', dateRange.to);
      const r = await fetch(`${API_URL}/api/admin/breaks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = r.ok ? await r.json() : [];
      // For 'pending' tab, show only the rows that actually need a decision
      // (completed breaks that ran over the allowance). Other tabs show
      // every row the backend returned for that status.
      const filtered = statusTab === 'pending'
        ? d.filter(x => x.status === 'completed' && x.excess_duration_hours > 0)
        : d;
      setRecords(filtered);
      // Drop any selections that no longer exist in the new dataset.
      setSelected(prev => {
        const stillThere = new Set(filtered.map(r => r.id));
        const next = new Set();
        prev.forEach(id => { if (stillThere.has(id)) next.add(id); });
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [statusTab, typeFilter, geoFilter, dateRange.from, dateRange.to]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Silent 30-sec auto-refresh.
  useAutoRefresh(fetchAll, 30_000);

  const counts = useMemo(() => {
    // counts reflect the current filter set, just bucketed by status
    const c = { pending: 0, approved: 0, waived: 0, rejected: 0, all: records.length };
    for (const r of records) {
      const st = r.approval?.status;
      if (st && st in c) c[st]++;
    }
    return c;
  }, [records]);

  const action = async (id, act) => {
    const token = localStorage.getItem('token');
    const body = { action: act, notes: notes || '' };
    if (act === 'approve' && editing === id) body.penalty_hours = parseFloat(penalty || 0);
    const r = await fetch(`${API_URL}/api/admin/breaks/${id}/approve`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      setEditing(null); setPenalty(''); setNotes('');
      fetchAll();
    } else {
      alert('Action failed');
    }
  };

  // ---- Bulk select / bulk action ---------------------------------------- //
  // Only PENDING rows can be acted on, so they're the only ones selectable.
  const selectablePendingRows = useMemo(
    () => records.filter(r => (r.approval?.status || 'pending') === 'pending'),
    [records]
  );
  const allSelectableSelected = selectablePendingRows.length > 0
    && selectablePendingRows.every(r => selected.has(r.id));
  const someSelected = selected.size > 0;

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectablePendingRows.map(r => r.id)));
    }
  };

  const bulkApply = async (act) => {
    if (!someSelected) return;
    const ids = Array.from(selected);
    const label = act === 'approve' ? 'Approve' : act === 'waive' ? 'Waive' : 'Reject';
    if (!window.confirm(`${label} ${ids.length} break record${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkWorking(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/breaks/bulk-approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, action: act, notes: bulkNotes || '' }),
      });
      if (r.ok) {
        const d = await r.json();
        alert(`${label} done · ${d.updated} record${d.updated === 1 ? '' : 's'} · ${d.total_penalty_hours}h total penalty`);
        setSelected(new Set());
        setBulkNotes('');
        fetchAll();
      } else {
        alert('Bulk action failed');
      }
    } finally {
      setBulkWorking(false);
    }
  };

  return (
    <div data-testid="break-approvals">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Break Approvals</h1>
          <p className="text-xs text-gray-500">{records.length} records · {dateRange.from} → {dateRange.to}{geoFilter && geoOptions.find(g => g.id === geoFilter) ? ` · ${geoOptions.find(g => g.id === geoFilter).name}` : ''}</p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm"
          data-testid="break-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Top filter bar (always visible) */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-3 items-end mb-3" data-testid="break-top-filters">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter size={14} /> Filters</div>

        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>Geo</span>
          <select
            className="px-3 py-1.5 border rounded text-sm min-w-[160px]"
            value={geoFilter}
            onChange={(e) => setGeoFilter(e.target.value)}
            data-testid="break-filter-geo"
          >
            <option value="">All Geo Fences</option>
            {geoOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>

        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>From</span>
          <input
            type="date" value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="px-2 py-1.5 border rounded text-sm w-40"
            data-testid="break-filter-from"
          />
        </label>
        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>To</span>
          <input
            type="date" value={dateRange.to} min={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="px-2 py-1.5 border rounded text-sm w-40"
            data-testid="break-filter-to"
          />
        </label>

        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>Type</span>
          <select
            className="px-3 py-1.5 border rounded text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="break-filter-type"
          >
            <option value="all">All types</option>
            <option value="lunch">Lunch</option>
            <option value="tea">Tea</option>
            <option value="permission">Permission</option>
            <option value="dinner">Dinner</option>
          </select>
        </label>

        <button
          onClick={() => { setGeoFilter(''); setTypeFilter('all'); setDateRange({ from: daysAgoISO(6), to: todayISO() }); }}
          className="text-xs text-gray-600 hover:text-gray-900 underline ml-auto"
          data-testid="break-filter-clear"
        >Reset</button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="break-status-tabs">
        {STATUS_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setStatusTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              statusTab === t.id ? `${t.tone} font-semibold` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid={`break-tab-${t.id}`}
          >
            {t.label} <span className="ml-1 opacity-70">({counts[t.id] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Bulk action toolbar — appears when at least one row is ticked */}
      {someSelected && (
        <div className="bg-indigo-600 text-white rounded-xl px-3 py-2 mb-3 flex flex-wrap items-center gap-2" data-testid="break-bulk-bar">
          <span className="font-semibold">
            {selected.size} selected
          </span>
          <input
            type="text"
            placeholder="Notes for all selected (optional)"
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            className="flex-1 min-w-[180px] px-2 py-1 text-sm rounded text-gray-900"
            data-testid="break-bulk-notes"
          />
          <button
            onClick={() => bulkApply('approve')}
            disabled={bulkWorking}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 rounded text-sm font-semibold"
            data-testid="break-bulk-approve"
          ><CheckCircle size={14} /> Approve all</button>
          <button
            onClick={() => bulkApply('waive')}
            disabled={bulkWorking}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 rounded text-sm font-semibold"
            data-testid="break-bulk-waive"
          ><ClockIcon size={14} /> Waive all</button>
          <button
            onClick={() => bulkApply('reject')}
            disabled={bulkWorking}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 rounded text-sm font-semibold"
            data-testid="break-bulk-reject"
          ><XCircle size={14} /> Reject all</button>
          <button
            onClick={() => { setSelected(new Set()); setBulkNotes(''); }}
            disabled={bulkWorking}
            className="px-3 py-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-60 rounded text-sm"
            data-testid="break-bulk-clear"
          >Clear</button>
        </div>
      )}

      {/* One-line table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="break-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b text-[11px] uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-center w-8">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    disabled={selectablePendingRows.length === 0}
                    onChange={toggleSelectAll}
                    className="cursor-pointer"
                    title={selectablePendingRows.length ? 'Select all pending' : 'No pending rows to select'}
                    data-testid="break-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Staff</th>
                <th className="px-3 py-2 text-left">Geo</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-center">Allowed</th>
                <th className="px-3 py-2 text-center">Actual</th>
                <th className="px-3 py-2 text-center">Excess</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-center">Penalty</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && records.length === 0 && (
                <tr><td colSpan="11" className="px-3 py-8 text-center text-gray-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan="11" className="px-3 py-8 text-center text-gray-500">No break records match the current filters.</td></tr>
              )}
              {records.map(r => {
                const st = r.approval?.status || 'pending';
                const isEditing = editing === r.id;
                const isSelectable = st === 'pending';
                const isChecked = selected.has(r.id);
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`hover:bg-blue-50 ${isChecked ? 'bg-indigo-50' : ''}`} data-testid={`break-row-${r.id}`}>
                      <td className="px-3 py-2 text-center">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleOne(r.id)}
                            className="cursor-pointer"
                            data-testid={`break-select-${r.id}`}
                          />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-medium">{r.staff_name}</span>
                        {r.staff_code && <span className="ml-1 text-[11px] text-gray-400 font-mono">{r.staff_code}</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {r.geo_fence_name ? (
                          <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-gray-400" /> {r.geo_fence_name}</span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-[11px] rounded-full capitalize ${TYPE_TONE[r.type] || 'bg-gray-100 text-gray-700'}`}>
                          {r.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums">{formatDuration(r.allowed_duration_hours)}</td>
                      <td className="px-3 py-2 text-center tabular-nums">{formatDuration(r.duration_hours)}</td>
                      <td className="px-3 py-2 text-center tabular-nums">
                        {(r.excess_duration_hours || 0) > 0 ? (
                          <span className="text-red-600 font-semibold">{formatDuration(r.excess_duration_hours)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 text-[11px] rounded-full capitalize ${STATUS_BADGE[st] || 'bg-gray-100 text-gray-700'}`}>
                          {st}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums">
                        {(r.approval?.penalty_hours || 0) > 0 ? (
                          <span className="text-red-600">{formatDuration(r.approval.penalty_hours)}</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {st === 'pending' && !isEditing && (
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => { setEditing(r.id); setPenalty(r.excess_duration_hours || 0); setNotes(''); }}
                              className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded"
                              title="Approve with penalty"
                              data-testid={`break-approve-${r.id}`}
                            ><CheckCircle size={14} /></button>
                            <button
                              onClick={() => action(r.id, 'waive')}
                              className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded"
                              title="Waive (no penalty)"
                              data-testid={`break-waive-${r.id}`}
                            ><ClockIcon size={14} /></button>
                            <button
                              onClick={() => action(r.id, 'reject')}
                              className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded"
                              title="Reject (full penalty)"
                              data-testid={`break-reject-${r.id}`}
                            ><XCircle size={14} /></button>
                          </div>
                        )}
                        {st !== 'pending' && r.approval?.notes && (
                          <span className="text-[11px] text-gray-500 italic truncate max-w-[160px] inline-block" title={r.approval.notes}>
                            {r.approval.notes}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-amber-50" data-testid={`break-edit-${r.id}`}>
                        <td colSpan="11" className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-xs text-gray-600">Penalty hrs:</span>
                            <input
                              type="number" step="0.05" min="0"
                              value={penalty}
                              onChange={(e) => setPenalty(e.target.value)}
                              className="w-24 px-2 py-1 border rounded text-sm"
                              data-testid={`break-penalty-${r.id}`}
                            />
                            <input
                              type="text" placeholder="Notes (optional)"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className="flex-1 min-w-[180px] px-2 py-1 border rounded text-sm"
                              data-testid={`break-notes-${r.id}`}
                            />
                            <button
                              onClick={() => action(r.id, 'approve')}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold"
                              data-testid={`break-confirm-${r.id}`}
                            >Confirm Approve</button>
                            <button
                              onClick={() => { setEditing(null); setPenalty(''); setNotes(''); }}
                              className="px-3 py-1.5 bg-gray-200 rounded text-sm"
                            >Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
